/**
 * ============================================================================
 * Order Extractor V2 API Client
 * ============================================================================
 * Version: 1.0.0
 * Created: 2025-12-20
 * 
 * Description:
 *   API client for Order Extractor V2 with smart sync-on-demand
 * 
 * ============================================================================
 */

import apiClient from './client';



export interface OrderExportRequest {
    date_from: string;  // YYYY-MM-DD
    date_to: string;    // YYYY-MM-DD
    status_filter?: string[];
    source_filter?: string[];
    order_ids?: number[];  // For exporting selected orders only
}

export interface SyncStats {
    missing_count: number;
    changed_count: number;
    synced_count: number;
}

export interface OrderExportResponse {
    orders: any[];
    total_count: number;
    sync_stats: SyncStats;
    processing_time_seconds: number;
}

/**
 * Get orders with smart sync-on-demand
 */
export const getOrdersWithSmartSync = async (
    request: OrderExportRequest
): Promise<OrderExportResponse> => {
    const response = await apiClient.post<OrderExportResponse>(
        '/order-extractor-v2/orders',
        request
    );
    return response.data;
};

/**
 * Export orders to Excel file
 */
export const exportOrdersToExcel = async (
    request: OrderExportRequest
): Promise<Blob> => {
    const response = await apiClient.post(
        '/order-extractor-v2/export',
        request,
        {
            responseType: 'blob'
        }
    );
    return response.data;
};

/**
 * Download Excel file
 */
export const downloadExcelFile = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};

/**
 * Export item list to PDF file (B2C Item List - Portrait)
 */
export const exportItemListToPDF = async (
    request: OrderExportRequest
): Promise<Blob> => {
    const response = await apiClient.post(
        '/order-extractor-v2/export-pdf-items',
        request,
        {
            responseType: 'blob'
        }
    );
    return response.data;
};

/**
 * Export order list to PDF file (B2C Order List - Landscape)
 */
export const exportOrderListToPDF = async (
    request: OrderExportRequest
): Promise<Blob> => {
    const response = await apiClient.post(
        '/order-extractor-v2/export-pdf-orders',
        request,
        {
            responseType: 'blob'
        }
    );
    return response.data;
};


/**
 * Download PDF file
 */
export const downloadPDFFile = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};

/**
 * Mark orders as downloaded for packaging
 */
export const markOrdersAsDownloaded = async (
    orderIds: number[]
): Promise<{ message: string, count: number }> => {
    const response = await apiClient.post(
        '/order-extractor-v2/mark-downloaded',
        { order_ids: orderIds }
    );
    return response.data;
};

